import { useMemo } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment-timezone";
import { toast } from "sonner";
import * as z from "zod";

import ACL_RELATIONSHIPS from "acl-relationships";
import PhoneNumbersInput from "components/PhoneNumbersInput";
import Validate from "components/Validate";
import { useRegionOptions } from "hooks/useRegionOptions";
import { useUserSettings } from "hooks/useUserSettings";
import AuthService from "services/Auth";
import UserService from "services/User";
import ProfilePictureUpload from "routes/user/ProfilePictureUpload";
import { FormCombobox, FormInput } from "components/BaseForm";
import { SaveButton } from "components/ui/save-button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Separator } from "components/ui/separator";
import { Skeleton } from "components/ui/skeleton";
import useAuthorized from "hooks/useAuthorized";

const attachmentSchema = z.object({
  s3_key: z.string(),
  file_name: z.string(),
  file_size: z.number(),
  file_content_type: z.string().refine(Validate.imageFileType, {
    message: "Invalid image type. Supported types: gif, jpg, png",
  }),
});

const phoneNumberSchema = z.object({
  id: z.number().optional(),
  is_primary: z.boolean(),
  number: z.string(),
  ext: z.string().nullable().optional(),
  type: z.number(),
  enabled: z.boolean(),
  is_callable: z.boolean(),
});

const profileSchema = z.object({
  name: z.string().min(1, "Name is required"),
  title: z.string(),
  email: z
    .string()
    .min(1, "Email is required")
    .refine(Validate.email, "Invalid email format"),
  preferredName: z.string(),
  department: z.string(),
  timeZone: z.string(),
  region: z.number().nullable(),
  attachment: attachmentSchema.nullable().optional(),
  removeProfilePicture: z.boolean().default(false),
  phoneNumbers: z.array(phoneNumberSchema).default([]),
});

const DEFAULT_VALUES = {
  name: "",
  title: "",
  email: "",
  preferredName: "",
  department: "",
  timeZone: "",
  region: null,
  attachment: null,
  removeProfilePicture: false,
  phoneNumbers: [],
};

function buildTimeZoneOptions() {
  const names = moment.tz.names();
  const usPacificNewIdx = names.indexOf("US/Pacific-New");
  if (usPacificNewIdx !== -1) names.splice(usPacificNewIdx, 1);
  return names.map(zone => {
    const offset = moment.tz(zone).format("Z");
    return { key: zone, value: zone, text: `${zone} (${offset})` };
  });
}

export function UserProfile() {
  const canEdit = useAuthorized([ACL_RELATIONSHIPS.userSettings.edit]);
  const timeZoneOptions = useMemo(() => buildTimeZoneOptions(), []);
  const regionOptions = useRegionOptions();
  const { data: settings, isLoading, refetch } = useUserSettings();

  const profilePicture =
    settings?.user_settings?.account?.profile_picture ?? null;

  const values = useMemo(() => {
    if (!settings) return DEFAULT_VALUES;
    const account = settings.user_settings?.account ?? {};
    return {
      name: settings.full_name || "",
      title: settings.title || "",
      email: settings.email || "",
      preferredName: account.preferred_name || "",
      department: account.department || "",
      timeZone: account.time_zone_name || "",
      region: settings.region_id ?? null,
      attachment: null,
      removeProfilePicture: false,
      phoneNumbers: settings.phone_numbers?.map(pn => ({ ...pn })) ?? [],
    };
  }, [settings]);

  const form = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: DEFAULT_VALUES,
    values,
    disabled: !canEdit,
  });
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting, isDirty },
  } = form;

  const name = useWatch({ control, name: "name" });
  const removeProfilePicture = useWatch({
    control,
    name: "removeProfilePicture",
  });

  const handlePictureChange = ({ attachment }) => {
    setValue("attachment", attachment, { shouldDirty: true });
    setValue("removeProfilePicture", false, { shouldDirty: true });
  };

  const handlePictureClear = () => {
    setValue("attachment", null, { shouldDirty: true });
    setValue("removeProfilePicture", true, { shouldDirty: true });
  };

  const onSubmit = async values => {
    try {
      await UserService.editUserSettings({
        name: values.name || undefined,
        title: values.title || undefined,
        email: values.email || undefined,
        preferredName: values.preferredName || undefined,
        department: values.department || undefined,
        timeZone: values.timeZone
          ? moment.tz(values.timeZone).format("Z")
          : undefined,
        timeZoneName: values.timeZone || undefined,
        region: values.region,
        attachment: values.attachment || undefined,
        removeProfilePicture: values.removeProfilePicture || undefined,
        phoneNumbers: values.phoneNumbers.filter(pn => pn.number?.trim()),
      });
      if (values.name) AuthService.setName(values.name);
      if (values.email) AuthService.setEmail(values.email);
      if (values.timeZone) AuthService.setTimeZone(values.timeZone);
      await refetch();
      toast.success("Settings update successful");
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <>
      <ProfilePictureUpload
        value={removeProfilePicture ? null : profilePicture}
        name={name}
        disabled={!canEdit}
        onChange={handlePictureChange}
        onClear={handlePictureClear}
      />
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <CardHeader>
              <CardTitle>
                {isLoading ? (
                  <Skeleton className="tw:h-6 tw:w-24" />
                ) : (
                  "Profile"
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="tw:flex tw:flex-col tw:gap-5">
              <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
                <FormInput name="name" label="Name" required />
                <FormInput name="preferredName" label="Preferred Name" />
                <FormInput name="title" label="Title" />
                <FormInput name="department" label="Department" />
                <FormInput name="email" label="Email" required disabled />
                <FormCombobox
                  name="timeZone"
                  label="Time Zone"
                  options={timeZoneOptions}
                />
                <FormCombobox
                  name="region"
                  label="Region"
                  options={regionOptions}
                  clearable
                />
              </div>

              <Separator />

              <Controller
                name="phoneNumbers"
                control={control}
                render={({ field }) => (
                  <PhoneNumbersInput
                    type="user"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </CardContent>

            <CardFooter className="tw:flex tw:justify-end">
              <SaveButton
                type="submit"
                saving={isSubmitting}
                disabled={!isDirty}
              />
            </CardFooter>
          </Card>
        </form>
      </FormProvider>
    </>
  );
}
